import React, {useMemo, useState} from 'react';
import {Menu} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import fileDownload from 'js-file-download';
import type {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_Updater,
} from 'mantine-react-table';
import type {
  CourseAssignmentFilter,
  CourseAssignmentLatenessBucketEnum,
  CourseAssignmentSimpleResponse,
  CourseAssignmentStateEnum,
} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {
  CourseAssignmentStatePill,
  CourseAssignmentTypePill,
} from 'src/shared/constants/PillConstants';
import {ProfileAvatar} from 'src/app/personnel/ProfileAvatar';
import {
  useBulkExtendCourseAssignmentsMutation,
  useBulkRemindCourseAssignmentsMutation,
  useBulkWaiveCourseAssignmentsMutation,
  useExportCourseAssignmentsMutation,
  useGetCourseAssignmentListV2Query,
} from 'src/redux/queries/courseAssignment';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {
  CourseAssignmentStateDataOptions,
  CourseAssignmentTypeDataOptions,
} from 'src/shared/constants/columnFilterData';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {ComplianceBulkExtendDialog} from './ComplianceBulkExtendDialog';
import {ComplianceBulkWaiveDialog} from './ComplianceBulkWaiveDialog';
import type {ComplianceFilters} from './ComplianceFiltersBar';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ComplianceAssignmentsGridProps {
  filters: ComplianceFilters;
  latenessBucket: CourseAssignmentLatenessBucketEnum | undefined;
  onAggregateRefetch: () => void;
}

const formatDate = (value: Date | string | undefined | null): string =>
  value ? parseUtcDate(value).toLocaleDateString() : '';

const toBulkFilter = (
  filters: ComplianceFilters,
  latenessBucket: CourseAssignmentLatenessBucketEnum | undefined
): CourseAssignmentFilter => ({
  courseDefinitionIds: filters.courseDefinitionIds.length
    ? filters.courseDefinitionIds.map(Number)
    : undefined,
  departmentId: filters.departmentId ? Number(filters.departmentId) : undefined,
  supervisorId: filters.supervisorId ? Number(filters.supervisorId) : undefined,
  vendorId: filters.vendorId ? Number(filters.vendorId) : undefined,
  projectId: filters.projectId ? Number(filters.projectId) : undefined,
  userTypes: filters.userTypes.length ? filters.userTypes : undefined,
  latenessBucket,
  activeStates: [true],
});

/**
 * Cross-course assignments grid for the Compliance Cockpit. Two selection
 * modes: explicit (default) tracks individually ticked row ids across
 * pagination and sends them as `includedIds`; "Select all N matching"
 * escalates to the current `ComplianceFiltersBar` filter plus the active
 * lateness-bucket stat-card filter, with unticked rows tracked as
 * `excludedIds` and an empty `includedIds` — the shared bulk-selection
 * contract established in Wave 1/2 (docs/plans/training-admin-overhaul.md,
 * Wave 2).
 */
export const ComplianceAssignmentsGrid: React.FC<
  ComplianceAssignmentsGridProps
> = ({filters, latenessBucket, onAggregateRefetch}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [exportCourseAssignments] = useExportCourseAssignmentsMutation();
  const [bulkRemindCourseAssignments, {isLoading: isRemindLoading}] =
    useBulkRemindCourseAssignmentsMutation();
  const [bulkExtendCourseAssignments, {isLoading: isExtendLoading}] =
    useBulkExtendCourseAssignmentsMutation();
  const [bulkWaiveCourseAssignments, {isLoading: isWaiveLoading}] =
    useBulkWaiveCourseAssignmentsMutation();
  const [isExportLoading, setIsExportLoading] = useState(false);
  const {hasPermission: hasUpdatePermission} = useHasModulePermission({
    module: ModuleEnum.Training,
    operation: PermissionOperationEnum.Update,
  });

  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const [confirmRemindOpened, confirmRemindHandlers] = useDisclosure(false);
  const [extendDialogOpened, extendDialogHandlers] = useDisclosure(false);
  const [waiveDialogOpened, waiveDialogHandlers] = useDisclosure(false);

  const columns = useMemo<MRT_ColumnDef<CourseAssignmentSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'assignedTo.avatarUrl',
        header: '',
        enableColumnFilter: false,
        enableSorting: false,
        enableClickToCopy: false,
        enableColumnActions: false,
        size: 50,
        enableResizing: false,
        Cell: ({cell}) => (
          <ProfileAvatar alt="Avatar" src={cell.getValue<string>()} />
        ),
      },
      {accessorKey: 'assignedTo.name', header: 'Person', enableResizing: false},
      {accessorKey: 'assignedTo.employeeId', header: 'Employee ID'},
      {
        accessorKey: 'courseDefinition.name',
        header: 'Course',
        enableResizing: false,
      },
      {
        accessorKey: 'state',
        header: 'State',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseAssignmentStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill
            {...CourseAssignmentStatePill(
              cell.getValue<CourseAssignmentStateEnum>()
            )}
          />
        ),
      },
      {
        accessorKey: 'isRenewal',
        header: 'Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseAssignmentTypeDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...CourseAssignmentTypePill(cell.getValue<boolean>())} />
        ),
      },
      {
        id: 'dueAt',
        accessorFn: row => (row.dueAt ? parseUtcDate(row.dueAt) : null),
        header: 'Due',
        Cell: ({cell}) => formatDate(cell.getValue<Date | null>()),
      },
      {
        accessorKey: 'daysPastDue',
        header: 'Days Past Due',
        // Computed post-query on the backend; server-side column filtering
        // cannot translate it (sorting is rewritten to DueAt server-side).
        enableColumnFilter: false,
        Cell: ({cell}) => {
          const value = cell.getValue<number | undefined>();
          return value !== undefined && value !== null ? (
            <Box c={value > 0 ? 'red' : undefined}>{value}</Box>
          ) : (
            ''
          );
        },
      },
      {
        id: 'expectedUnlockAt',
        accessorFn: row =>
          row.expectedUnlockAt ? parseUtcDate(row.expectedUnlockAt) : null,
        header: 'Unlocks',
        Cell: ({cell}) => formatDate(cell.getValue<Date | null>()),
      },
      {
        id: 'expectedExpirationAt',
        accessorFn: row =>
          row.expectedExpirationAt
            ? parseUtcDate(row.expectedExpirationAt)
            : null,
        header: 'Expires',
        Cell: ({cell}) => formatDate(cell.getValue<Date | null>()),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-compliance-assignments',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'daysPastDue', desc: true}],
  });
  const {
    start,
    length,
    filters: mrtFilters,
    filterFns,
    sorting,
    columnFilters,
    globalFilter,
  } = tableState;

  const hasActiveTableFilter =
    columnFilters.length > 0 || globalFilter.trim().length > 0;

  const apiRequest = {
    start,
    length,
    filters: mrtFilters,
    filterFns,
    sorting,
    courseDefinitionIds: filters.courseDefinitionIds.length
      ? filters.courseDefinitionIds.map(Number)
      : undefined,
    departmentId: filters.departmentId
      ? Number(filters.departmentId)
      : undefined,
    supervisorId: filters.supervisorId
      ? Number(filters.supervisorId)
      : undefined,
    vendorId: filters.vendorId ? Number(filters.vendorId) : undefined,
    projectId: filters.projectId ? Number(filters.projectId) : undefined,
    userTypes: filters.userTypes.length ? filters.userTypes : undefined,
    latenessBucket,
    activeStates: [true],
  };

  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseAssignmentListV2Query(apiRequest);
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const selectedCount = selectAllMatching
    ? Math.max(totalCount - excludedIds.size, 0)
    : selectedIds.size;

  // Escalating to "select all matching" is only a meaningful next step once
  // there is an active explicit selection and more matching rows exist than
  // are visible on the current page.
  const canEscalateToSelectAllMatching =
    !selectAllMatching && selectedIds.size > 0 && totalCount > rows.length;

  const rowSelection: MRT_RowSelectionState = {};
  rows.forEach(row => {
    if (row.id === undefined) {
      return;
    }
    const isSelected = selectAllMatching
      ? !excludedIds.has(row.id)
      : selectedIds.has(row.id);
    if (isSelected) {
      rowSelection[String(row.id)] = true;
    }
  });

  const handleRowSelectionChange = (
    updater: MRT_Updater<MRT_RowSelectionState>
  ) => {
    const newSelection =
      typeof updater === 'function' ? updater(rowSelection) : updater;
    if (selectAllMatching) {
      setExcludedIds(previous => {
        const next = new Set(previous);
        rows.forEach(row => {
          if (row.id === undefined) {
            return;
          }
          if (newSelection[String(row.id)]) {
            next.delete(row.id);
          } else {
            next.add(row.id);
          }
        });
        return next;
      });
      return;
    }
    setSelectedIds(previous => {
      const next = new Set(previous);
      rows.forEach(row => {
        if (row.id === undefined) {
          return;
        }
        if (newSelection[String(row.id)]) {
          next.add(row.id);
        } else {
          next.delete(row.id);
        }
      });
      return next;
    });
  };

  const handleSelectAllMatching = () => {
    setSelectAllMatching(true);
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  };

  const handleClearSelection = () => {
    setSelectAllMatching(false);
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  };

  const handleRemind = async () => {
    confirmRemindHandlers.close();
    try {
      await bulkRemindCourseAssignments({
        filter: toBulkFilter(filters, latenessBucket),
        excludedIds: selectAllMatching ? Array.from(excludedIds) : [],
        includedIds: selectAllMatching ? [] : Array.from(selectedIds),
      }).unwrap();
      setAlert(`Reminders queued for ${selectedCount} assignments.`, 'success');
      handleClearSelection();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleExtend = async (payload: {
    extendByDays?: number;
    newDueAt?: Date;
  }) => {
    try {
      await bulkExtendCourseAssignments({
        filter: toBulkFilter(filters, latenessBucket),
        excludedIds: selectAllMatching ? Array.from(excludedIds) : [],
        includedIds: selectAllMatching ? [] : Array.from(selectedIds),
        extendByDays: payload.extendByDays,
        newDueAt: payload.newDueAt,
      }).unwrap();
      extendDialogHandlers.close();
      setAlert(
        `Extension queued for ${selectedCount} assignments. Due dates update in the background.`,
        'success'
      );
      handleClearSelection();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleWaive = async (payload: {reason: string; expiresAt?: Date}) => {
    try {
      await bulkWaiveCourseAssignments({
        filter: toBulkFilter(filters, latenessBucket),
        excludedIds: selectAllMatching ? Array.from(excludedIds) : [],
        includedIds: selectAllMatching ? [] : Array.from(selectedIds),
        reason: payload.reason,
        expiresAt: payload.expiresAt,
      }).unwrap();
      waiveDialogHandlers.close();
      setAlert(
        `Waiver queued for ${selectedCount} assignments. Matching rule exceptions and assignment deactivation update in the background.`,
        'success'
      );
      handleClearSelection();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleExport = async () => {
    try {
      setIsExportLoading(true);
      const response = await exportCourseAssignments(apiRequest).unwrap();
      fileDownload(
        response!.data,
        `training_compliance_export_${new Date().toString().toShortDate()}.csv`
      );
      setAlert('Successfully exported file.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsExportLoading(false);
  };

  const handleRefresh = () => {
    void refetch();
    onAggregateRefetch();
  };

  const {table} = useTable({
    ...tableState,
    rowSelection,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowActions: true,
      enableRowSelection: hasUpdatePermission,
      positionToolbarAlertBanner: 'none',
      columns,
      data: rows,
      getRowId: row => String(row.id),
      onRowSelectionChange: handleRowSelectionChange,
      renderRowActionMenuItems: ({row}) => (
        <>
          {row.original.courseDefinition?.id && (
            <Menu.Item
              onClick={() =>
                navigate(
                  PathUtility.TrainingCourseAssignmentView(
                    String(row.original.courseDefinition?.id),
                    String(row.original.id),
                    'content'
                  )
                )
              }>
              View Assignment
            </Menu.Item>
          )}
          <PermissionGuard
            module={ModuleEnum.Personnel}
            operation={PermissionOperationEnum.View}>
            <Menu.Item
              onClick={() =>
                window.open(
                  PathUtility.PersonnelView(
                    String(row.original.assignedTo?.id),
                    'training'
                  ),
                  '_blank'
                )
              }>
              View Person (Opens in new tab)
            </Menu.Item>
          </PermissionGuard>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {hasUpdatePermission && selectedCount > 0 && (
              <>
                <Text size="sm" fw={500}>
                  {selectedCount} selected
                </Text>
                {canEscalateToSelectAllMatching && (
                  <TableToolbar.Action
                    variant="secondary"
                    onClick={handleSelectAllMatching}
                    disabled={hasActiveTableFilter}
                    tooltip={
                      hasActiveTableFilter
                        ? "Clear column filters to select across all pages (column filters don't apply to bulk actions)"
                        : undefined
                    }>
                    {`Select all ${totalCount} matching`}
                  </TableToolbar.Action>
                )}
                <TableToolbar.Action
                  variant="cancel"
                  onClick={handleClearSelection}>
                  Clear selection
                </TableToolbar.Action>
                <TableToolbar.Action
                  variant="secondary"
                  onClick={confirmRemindHandlers.open}
                  loading={isRemindLoading}>
                  Remind
                </TableToolbar.Action>
                <TableToolbar.Action
                  variant="secondary"
                  onClick={extendDialogHandlers.open}
                  loading={isExtendLoading}>
                  Extend
                </TableToolbar.Action>
                <TableToolbar.Action
                  variant="secondary"
                  onClick={waiveDialogHandlers.open}
                  loading={isWaiveLoading}>
                  Waive
                </TableToolbar.Action>
              </>
            )}
            <TableToolbar.Action
              variant="export"
              onClick={handleExport}
              loading={isExportLoading}
            />
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: handleRefresh,
  });

  return (
    <>
      {table}
      <ConfirmDialog
        opened={confirmRemindOpened}
        onClose={confirmRemindHandlers.close}
        onConfirm={handleRemind}
        confirmationButtonColor="yellow"
        title="Send reminders?"
        body={`This will send a reminder notification to ${selectedCount} assignment recipient${
          selectedCount === 1 ? '' : 's'
        }.`}
      />
      <ComplianceBulkExtendDialog
        opened={extendDialogOpened}
        selectedCount={selectedCount}
        isSaving={isExtendLoading}
        onClose={extendDialogHandlers.close}
        onConfirm={handleExtend}
      />
      <ComplianceBulkWaiveDialog
        opened={waiveDialogOpened}
        selectedCount={selectedCount}
        isSaving={isWaiveLoading}
        onClose={waiveDialogHandlers.close}
        onConfirm={handleWaive}
      />
    </>
  );
};
